import axios from 'src/utils/axios';
import helpers from 'src/helpers';
import { NOTIFICATION_STATUS } from 'src/constants';
import { addNotification } from 'src/actions/notificationActions';

// CON-1569 — combined TCR + Port admin screen actions.
//
// Backend endpoints are AND-gated on ADMIN_TOOLS_VIEW +
// TCR_REGISTRATIONS_ADMIN (list, notes) or TCR_RESUBMIT_ADMIN
// (submit/resubmit). The submit/resubmit thunks return the response data
// so the dry-run modal can hold its transient preview state locally
// while loading/error bookkeeping stays in the slice.

// ── Action Types ──

export const GET_TCR_PORT_ADMIN_LIST = '@tcr-port-admin/get-list';
export const GET_TCR_PORT_ADMIN_LIST_SUCCESS = '@tcr-port-admin/get-list-success';
export const GET_TCR_PORT_ADMIN_LIST_ERROR = '@tcr-port-admin/get-list-error';

export const GET_TCR_PORT_ADMIN_NOTES = '@tcr-port-admin/get-notes';
export const GET_TCR_PORT_ADMIN_NOTES_SUCCESS = '@tcr-port-admin/get-notes-success';
export const GET_TCR_PORT_ADMIN_NOTES_ERROR = '@tcr-port-admin/get-notes-error';

export const CREATE_TCR_PORT_ADMIN_NOTE = '@tcr-port-admin/create-note';
export const CREATE_TCR_PORT_ADMIN_NOTE_SUCCESS = '@tcr-port-admin/create-note-success';
export const CREATE_TCR_PORT_ADMIN_NOTE_ERROR = '@tcr-port-admin/create-note-error';

export const UPDATE_TCR_PORT_ADMIN_NOTE = '@tcr-port-admin/update-note';
export const UPDATE_TCR_PORT_ADMIN_NOTE_SUCCESS = '@tcr-port-admin/update-note-success';
export const UPDATE_TCR_PORT_ADMIN_NOTE_ERROR = '@tcr-port-admin/update-note-error';

export const DELETE_TCR_PORT_ADMIN_NOTE = '@tcr-port-admin/delete-note';
export const DELETE_TCR_PORT_ADMIN_NOTE_SUCCESS = '@tcr-port-admin/delete-note-success';
export const DELETE_TCR_PORT_ADMIN_NOTE_ERROR = '@tcr-port-admin/delete-note-error';

export const TCR_PORT_ADMIN_SUBMIT = '@tcr-port-admin/submit';
export const TCR_PORT_ADMIN_SUBMIT_DONE = '@tcr-port-admin/submit-done';

// ── Action Creators ──

/** *************************************************************************
 * getTcrPortAdminList
 *
 * GET /api/accountsettings/tcr/admin/list
 * Loads one page of combined TCR + Port rows. `page` is 1-indexed here
 * (the view converts from the 0-indexed TablePagination value).
 *************************************************************************** */
export const getTcrPortAdminList = ({
  q, status, provider, portStatus, sortBy, sortDir, page, pageSize,
}) => async (dispatch) => {
  dispatch({ type: GET_TCR_PORT_ADMIN_LIST });
  try {
    const response = await axios.get('/api/accountsettings/tcr/admin/list', {
      params: {
        q, status, provider, portStatus, sortBy, sortDir, page, pageSize,
      },
    });
    dispatch({
      type: GET_TCR_PORT_ADMIN_LIST_SUCCESS,
      payload: {
        rows: response?.data?.rows || [],
        total: response?.data?.total || 0,
        noteAuthors: response?.data?.noteAuthors || [],
        tcrStatusOptions: response?.data?.tcrStatusOptions || [],
        portStatusOptions: response?.data?.portStatusOptions || [],
      },
    });
  } catch (e) {
    const { error, message } = e?.response?.data || {};
    dispatch({
      type: GET_TCR_PORT_ADMIN_LIST_ERROR,
      payload: {
        error: helpers.transform.toString(message || error || 'Failed to load TCR registrations'),
      },
    });
  }
};

/** *************************************************************************
 * getTcrPortAdminNotes
 *
 * GET /api/accountsettings/tcr/admin/notes/:salespersonId
 * Loads the notes for one salesperson's expand-row timeline.
 *************************************************************************** */
export const getTcrPortAdminNotes = (salespersonId) => async (dispatch) => {
  dispatch({ type: GET_TCR_PORT_ADMIN_NOTES, payload: { salespersonId } });
  try {
    const response = await axios.get(`/api/accountsettings/tcr/admin/notes/${salespersonId}`);
    dispatch({
      type: GET_TCR_PORT_ADMIN_NOTES_SUCCESS,
      payload: {
        salespersonId,
        notes: response?.data?.notes || [],
      },
    });
  } catch (e) {
    const { error, message } = e?.response?.data || {};
    dispatch(addNotification(
      GET_TCR_PORT_ADMIN_NOTES_ERROR,
      helpers.transform.toString(message || error || 'Could not load notes'),
      NOTIFICATION_STATUS.ERROR,
    ));
    dispatch({ type: GET_TCR_PORT_ADMIN_NOTES_ERROR, payload: { salespersonId } });
  }
};

/** *************************************************************************
 * createTcrPortAdminNote
 *
 * POST /api/accountsettings/tcr/admin/notes
 * Saves an admin-authored note (optional noteAt + parentType/parentId),
 * then refreshes that salesperson's notes.
 *************************************************************************** */
export const createTcrPortAdminNote = ({
  salespersonId, text, noteAt, parentType, parentId, authorLabel,
}) => async (dispatch) => {
  dispatch({ type: CREATE_TCR_PORT_ADMIN_NOTE });
  try {
    await axios.post('/api/accountsettings/tcr/admin/notes', {
      salespersonId, text, noteAt, parentType, parentId, authorLabel,
    });
    dispatch({ type: CREATE_TCR_PORT_ADMIN_NOTE_SUCCESS, payload: { salespersonId } });
    dispatch(getTcrPortAdminNotes(salespersonId));
    return true;
  } catch (e) {
    const { error, message } = e?.response?.data || {};
    dispatch(addNotification(
      CREATE_TCR_PORT_ADMIN_NOTE_ERROR,
      helpers.transform.toString(message || error || 'Could not save note'),
      NOTIFICATION_STATUS.ERROR,
    ));
    dispatch({ type: CREATE_TCR_PORT_ADMIN_NOTE_ERROR });
    return false;
  }
};

/** *************************************************************************
 * updateTcrPortAdminNote
 *
 * PUT /api/accountsettings/tcr/admin/notes/:id
 * Edits an admin note's text/noteAt (tcr_port_admin only), then
 * refreshes that salesperson's notes.
 *************************************************************************** */
export const updateTcrPortAdminNote = ({
  id, salespersonId, text, noteAt, authorLabel,
}) => async (dispatch) => {
  dispatch({ type: UPDATE_TCR_PORT_ADMIN_NOTE });
  try {
    await axios.put(`/api/accountsettings/tcr/admin/notes/${id}`, { text, noteAt, authorLabel });
    dispatch({ type: UPDATE_TCR_PORT_ADMIN_NOTE_SUCCESS, payload: { salespersonId } });
    dispatch(getTcrPortAdminNotes(salespersonId));
    return true;
  } catch (e) {
    const { error, message } = e?.response?.data || {};
    dispatch(addNotification(
      UPDATE_TCR_PORT_ADMIN_NOTE_ERROR,
      helpers.transform.toString(message || error || 'Could not update note'),
      NOTIFICATION_STATUS.ERROR,
    ));
    dispatch({ type: UPDATE_TCR_PORT_ADMIN_NOTE_ERROR });
    return false;
  }
};

/** *************************************************************************
 * deleteTcrPortAdminNote
 *
 * DELETE /api/accountsettings/tcr/admin/notes/:id
 * Soft-deletes an admin note (tcr_port_admin only), then refreshes that
 * salesperson's notes.
 *************************************************************************** */
export const deleteTcrPortAdminNote = ({ id, salespersonId }) => async (dispatch) => {
  dispatch({ type: DELETE_TCR_PORT_ADMIN_NOTE });
  try {
    await axios.delete(`/api/accountsettings/tcr/admin/notes/${id}`);
    dispatch({ type: DELETE_TCR_PORT_ADMIN_NOTE_SUCCESS, payload: { salespersonId } });
    dispatch(getTcrPortAdminNotes(salespersonId));
    return true;
  } catch (e) {
    const { error, message } = e?.response?.data || {};
    dispatch(addNotification(
      DELETE_TCR_PORT_ADMIN_NOTE_ERROR,
      helpers.transform.toString(message || error || 'Could not delete note'),
      NOTIFICATION_STATUS.ERROR,
    ));
    dispatch({ type: DELETE_TCR_PORT_ADMIN_NOTE_ERROR });
    return false;
  }
};

/** *************************************************************************
 * runTcrSubmitAction
 *
 * POST /api/accountsettings/tcr/admin/initial-submit-campaign (kind
 * 'initial') or /api/accountsettings/tcr/resubmit (kind 'resubmit'),
 * with dryRun true (preview) or false (real submit). Returns the
 * response data (or throws) so the dry-run modal drives its own
 * transient preview state — behavior is a faithful port of the original
 * page's flow.
 *************************************************************************** */
export const runTcrSubmitAction = (kind, salespersonId, dryRun) => async (dispatch) => {
  dispatch({ type: TCR_PORT_ADMIN_SUBMIT });
  try {
    const endpoint = kind === 'initial'
      ? '/api/accountsettings/tcr/admin/initial-submit-campaign'
      : '/api/accountsettings/tcr/resubmit';
    const response = await axios.post(endpoint, { salespersonId, dryRun });
    dispatch({ type: TCR_PORT_ADMIN_SUBMIT_DONE });
    return response.data;
  } catch (e) {
    dispatch({ type: TCR_PORT_ADMIN_SUBMIT_DONE });
    throw e;
  }
};

// Admin sole-prop REGISTRATION submit for users stuck pre-submit behind the
// Smarty address gate. fields = { personalAddress1, personalCity,
// stateProvince, postalCode, country, mobilePhoneVerificationPhone }.
// dryRun=true returns the resolved payload preview without any writes.
export const runTcrRegistrationSubmit = (salespersonId, fields, dryRun) => async (dispatch) => {
  dispatch({ type: TCR_PORT_ADMIN_SUBMIT });
  try {
    const response = await axios.post(
      '/api/accountsettings/tcr/admin/submit-registration',
      { salespersonId, ...fields, dryRun },
    );
    dispatch({ type: TCR_PORT_ADMIN_SUBMIT_DONE });
    return response.data;
  } catch (e) {
    dispatch({ type: TCR_PORT_ADMIN_SUBMIT_DONE });
    throw e;
  }
};

// Admin "Switch to CP texting", mode 'finish' (TCR ready: the consultant's
// own "Turn On CP Texting" path with the admin options) or 'early' (still
// waiting on TCR or a port: provider, role and forms migration only). The
// server starts the switch in the background and answers 202
// { started, jobId, startedAt }; poll getTcrCPTextingSwitchStatus. A 409
// means a switch for this user is already running; its jobId and
// startedAt are in the error response. Returns the response data or throws.
export const runTcrEnableCPTexting = (salespersonId, mode = 'finish') => async (dispatch) => {
  dispatch({ type: TCR_PORT_ADMIN_SUBMIT });
  try {
    const response = await axios.post(
      '/api/accountsettings/tcr/admin/enable-cp-texting',
      { salespersonId, mode },
    );
    dispatch({ type: TCR_PORT_ADMIN_SUBMIT_DONE });
    return response.data;
  } catch (e) {
    dispatch({ type: TCR_PORT_ADMIN_SUBMIT_DONE });
    throw e;
  }
};

// Polled status of a switch started above:
// { state: 'running' | 'succeeded' | 'failed' | 'none' | 'unknown', message }.
// Read-only; no store bookkeeping.
export const getTcrCPTextingSwitchStatus = ({
  salespersonId, jobId, since, mode,
}) => async () => {
  const response = await axios.get('/api/accountsettings/tcr/admin/enable-cp-texting/status', {
    params: {
      salespersonId, jobId, since, mode,
    },
  });
  return response.data;
};

// Read-only preflight for the switch confirmation: TCR / port / number
// state, Smart Actions due in the next 7 days, unprocessed Order
// Follow-Up, and whether order auto-process is on.
export const getTcrCPTextingSwitchPreflight = (salespersonId) => async () => {
  const response = await axios.get('/api/accountsettings/tcr/admin/enable-cp-texting/preflight', {
    params: { salespersonId },
  });
  return response.data;
};
